import React, { useState, useEffect, useMemo, useReducer, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Button,
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  SkipNext,
  SkipPrevious,
  FirstPage,
  LastPage,
  Refresh,
  Download,
  Visibility,
  VisibilityOff,
  GridOn,
  Label,
} from '@mui/icons-material';
import Plot from 'react-plotly.js';
import { Canvas } from '@react-three/fiber';
import { LabelLayer } from '../../labels/LabelLayer';
import type { Mode } from '../../labels/LabelLayer';
import LabelHUD from '../../labels/LabelHUD';
import { boxReducer, BoxAction } from '../../labels/boxReducer';
import { exportJSON, importJSON } from '../../labels/io';

interface Point3D {
  x: number;
  y: number;
  z: number;
}

type PointData = Point3D | [number, number, number];

interface MmWaveFrame {
  frame_index: number;
  action_label: number;
  points: PointData[];
  point_count: number;
}

interface MmWaveData {
  metadata: {
    total_frames: number;
    total_actions: number;
    action_labels: number[];
    file_names: string[];
    bounds: {
      x: [number, number];
      y: [number, number];
      z: [number, number];
    };
  };
  frames: MmWaveFrame[];
}


const MmWaveVisualization: React.FC = () => {
  const [data, setData] = useState<MmWaveData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [useDemoData, setUseDemoData] = useState(true);
  
  // Frame navigation
  const [currentFrame, setCurrentFrame] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1000); // ms per frame
  
  // Visualization settings
  const [pointSize, setPointSize] = useState(8); // Increased from 3 to 8
  const [pointOpacity, setPointOpacity] = useState(0.8);
  const [colorScheme, setColorScheme] = useState('viridis');
  const [showGrid, setShowGrid] = useState(true);
  const [showAxes, setShowAxes] = useState(true);
  const [backgroundColor] = useState('#000000');
  
  // Color by options
  const [colorBy, setColorBy] = useState<'action' | 'z_coordinate' | 'distance'>('action');
  
  // View settings
  const [cameraDistance, setCameraDistance] = useState(3); // Reduced from 5 to 3
  const [autoRotate, setAutoRotate] = useState(false);
  const [rotationSpeed, setRotationSpeed] = useState(0.5);
  
  // Filtering
  const [pointLimit] = useState(10000);
  const [,setSelectedActions] = useState<number[]>([]);
  const [xRange, setXRange] = useState<[number, number]>([-5, 5]);
  const [yRange, setYRange] = useState<[number, number]>([-5, 5]);
  const [zRange, setZRange] = useState<[number, number]>([0, 3]);
  
  // UI state
  const [showStatistics, setShowStatistics] = useState(false);
  const [bypassFiltering, setBypassFiltering] = useState(true);
  
  // Labeling state
  const [showLabeling, setShowLabeling] = useState(false);
  const [labelingMode, setLabelingMode] = useState<Mode>('idle');
  const [selectedBoxId, setSelectedBoxId] = useState<string|undefined>();
  const [, setIsDragging] = useState(false);
  const [labelBoxes, dispatchLabelAction] = useReducer(boxReducer, []);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const dataUrl = useDemoData ? '/mmwave_demo.json' : '/mmwave_pointcloud.json';
        console.log('Loading data from:', dataUrl);
        
        const response = await fetch(dataUrl);
        if (!response.ok) {
          throw new Error(`Failed to load mmWave point cloud data from ${dataUrl}`);
        }
        
        const mmwaveData: MmWaveData = await response.json();
        console.log('Data loaded successfully:', mmwaveData);
        console.log('Total frames:', mmwaveData.metadata.total_frames);
        console.log('First frame points:', mmwaveData.frames[0]?.point_count);
        
        setData(mmwaveData);
        
        // Initialize selected actions with all available actions
        const uniqueActions = Array.from(new Set(mmwaveData.metadata.action_labels));
        setSelectedActions(uniqueActions);
        
        // Set initial ranges based on data bounds
        const allPoints = mmwaveData.frames.flatMap(frame => frame.points);
        if (allPoints.length > 0) {
          console.log('Sample points:', allPoints.slice(0, 3));
          
          // Handle both array format [x, y, z] and object format {x, y, z}
          const xCoords = allPoints.map(p => Array.isArray(p) ? p[0] : p.x);
          const yCoords = allPoints.map(p => Array.isArray(p) ? p[1] : p.y);
          const zCoords = allPoints.map(p => Array.isArray(p) ? p[2] : p.z);
          
          console.log('Sample coordinates:', {
            x: xCoords.slice(0, 5),
            y: yCoords.slice(0, 5),
            z: zCoords.slice(0, 5)
          });
          
          const minX = Math.min(...xCoords);
          const maxX = Math.max(...xCoords);
          const minY = Math.min(...yCoords);
          const maxY = Math.max(...yCoords);
          const minZ = Math.min(...zCoords);
          const maxZ = Math.max(...zCoords);
          
          console.log('Data bounds:', { minX, maxX, minY, maxY, minZ, maxZ });
          
          // Check if bounds are valid (not NaN)
          if (!isNaN(minX) && !isNaN(maxX) && !isNaN(minY) && !isNaN(maxY) && !isNaN(minZ) && !isNaN(maxZ)) {
            setXRange([minX, maxX]);
            setYRange([minY, maxY]);
            setZRange([minZ, maxZ]);
          } else {
            console.warn('Invalid bounds detected, using default ranges');
            // Use the bounds from metadata if available
            if (mmwaveData.metadata.bounds) {
              setXRange([mmwaveData.metadata.bounds.x[0], mmwaveData.metadata.bounds.x[1]]);
              setYRange([mmwaveData.metadata.bounds.y[0], mmwaveData.metadata.bounds.y[1]]);
              setZRange([mmwaveData.metadata.bounds.z[0], mmwaveData.metadata.bounds.z[1]]);
            } else {
              // Fallback to default ranges
              setXRange([-5, 5]);
              setYRange([-5, 5]);
              setZRange([0, 3]);
            }
          }
        }
        
      } catch (err) {
        console.error('Error loading data:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [useDemoData]);

  // Animation effect
  useEffect(() => {
    if (!isPlaying || !data) return;
    
    const interval = setInterval(() => {
      setCurrentFrame(prev => {
        if (prev >= data.metadata.total_frames - 1) {
          setIsPlaying(false);
          return 0;
        }
        return prev + 1;
      });
    }, playbackSpeed);
    
    return () => clearInterval(interval);
  }, [isPlaying, data, playbackSpeed]);

  // Get current frame data
  const currentFrameData = useMemo(() => {
    if (!data || currentFrame >= data.frames.length) return null;
    return data.frames[currentFrame];
  }, [data, currentFrame]);

  // Filter points based on current settings
  const filteredPoints = useMemo(() => {
    if (!currentFrameData) return [];
    
    // First, get all points from current frame
    const allFramePoints = currentFrameData.points || [];
    console.log(`Frame ${currentFrame}: Total points in frame: ${allFramePoints.length}`);
    
    let coordinateFiltered = allFramePoints;
    
    // Apply coordinate filtering only if bypass is disabled
    if (!bypassFiltering) {
      coordinateFiltered = allFramePoints.filter(point => {
        // Handle both array format [x, y, z] and object format {x, y, z}
        const x = Array.isArray(point) ? point[0] : point.x;
        const y = Array.isArray(point) ? point[1] : point.y;
        const z = Array.isArray(point) ? point[2] : point.z;
        
        return x >= xRange[0] && x <= xRange[1] &&
               y >= yRange[0] && y <= yRange[1] &&
               z >= zRange[0] && z <= zRange[1];
      });
      console.log(`Frame ${currentFrame}: Points after coordinate filtering: ${coordinateFiltered.length}`);
    } else {
      console.log(`Frame ${currentFrame}: Bypassing coordinate filtering, using all ${allFramePoints.length} points`);
    }
    
    // Apply point limit
    const limitedPoints = coordinateFiltered.slice(0, pointLimit);
    console.log(`Frame ${currentFrame}: Points after limiting: ${limitedPoints.length}`);
    
    return limitedPoints;
  }, [currentFrameData, xRange, yRange, zRange, pointLimit, currentFrame, bypassFiltering]);

  // Prepare plot data
  const plotData = useMemo(() => {
    if (!filteredPoints.length) {
      console.log('No filtered points available for plotting');
      return [];
    }
    
    console.log(`Creating plot data with ${filteredPoints.length} points`);
    
    // Handle both array format [x, y, z] and object format {x, y, z}
    const x = filteredPoints.map(p => Array.isArray(p) ? p[0] : p.x);
    const y = filteredPoints.map(p => Array.isArray(p) ? p[1] : p.y);
    const z = filteredPoints.map(p => Array.isArray(p) ? p[2] : p.z);
    
    let colorValues: number[] = [];
    let colorbarTitle = '';
    
    switch (colorBy) {
      case 'action':
        colorValues = new Array(filteredPoints.length).fill(currentFrameData?.action_label || 0);
        colorbarTitle = 'Action Label';
        break;
      case 'z_coordinate':
        colorValues = z;
        colorbarTitle = 'Z Coordinate';
        break;
      case 'distance':
        colorValues = filteredPoints.map(p => {
          const px = Array.isArray(p) ? p[0] : p.x;
          const py = Array.isArray(p) ? p[1] : p.y;
          const pz = Array.isArray(p) ? p[2] : p.z;
          return Math.sqrt(px**2 + py**2 + pz**2);
        });
        colorbarTitle = 'Distance from Origin';
        break;
    }
    
    const plotData = [{
      type: 'scatter3d' as const,
      mode: 'markers' as const,
      x,
      y,
      z,
      marker: {
        size: pointSize,
        opacity: pointOpacity,
        color: colorValues,
        colorscale: colorScheme,
        showscale: true,
        colorbar: {
          title: colorbarTitle,
          titleside: 'right'
        }
      },
      hovertemplate: 
        '<b>Point</b><br>' +
        'X: %{x:.3f}<br>' +
        'Y: %{y:.3f}<br>' +
        'Z: %{z:.3f}<br>' +
        'Action: ' + (currentFrameData?.action_label || 'N/A') + '<br>' +
        '<extra></extra>'
    }];
    
    console.log('Plot data created:', plotData);
    return plotData;
  }, [filteredPoints, pointSize, pointOpacity, colorScheme, colorBy, currentFrameData]);

  // Plot layout
  const plotLayout = useMemo(() => ({
    title: {
      text: `mmWave Point Cloud - Frame ${currentFrame + 1}/${data?.metadata.total_frames || 0} (Action: ${currentFrameData?.action_label || 'N/A'})`,
      font: { color: '#ffffff' }
    },
    scene: {
      xaxis: {
        title: 'X Axis (Range)',
        showgrid: showGrid,
        gridcolor: '#333333',
        zeroline: showAxes,
        zerolinecolor: '#666666'
      },
      yaxis: {
        title: 'Y Axis (Azimuth)',
        showgrid: showGrid,
        gridcolor: '#333333',
        zeroline: showAxes,
        zerolinecolor: '#666666'
      },
      zaxis: {
        title: 'Z Axis (Elevation)',
        showgrid: showGrid,
        gridcolor: '#333333',
        zeroline: showAxes,
        zerolinecolor: '#666666'
      },
      camera: {
        eye: {
          x: 2,
          y: 2,
          z: 2
        },
        center: {
          x: 0,
          y: 0,
          z: 0
        }
      }
    },
    paper_bgcolor: backgroundColor,
    plot_bgcolor: backgroundColor,
    font: { color: '#ffffff' },
    margin: { l: 0, r: 0, t: 50, b: 0 },
    showlegend: false
  }), [currentFrame, data, currentFrameData, showGrid, showAxes, backgroundColor]);

  // Get action statistics
  const actionStats = useMemo(() => {
    if (!data) return [];
    
    const actionCounts = data.metadata.action_labels.reduce((acc, label) => {
      acc[label] = (acc[label] || 0) + 1;
      return acc;
    }, {} as Record<number, number>);
    
    return Object.entries(actionCounts).map(([label, count]) => ({
      label: parseInt(label),
      count,
      description: `Action ${label}`
    })).sort((a, b) => a.label - b.label);
  }, [data]);

  // Download current frame data
  const handleDownload = () => {
    if (!currentFrameData) return;
    
    const csvContent = [
      'x,y,z,action_label',
      ...filteredPoints.map(p => {
        // Handle both array format [x, y, z] and object format {x, y, z}
        const x = Array.isArray(p) ? p[0] : p.x;
        const y = Array.isArray(p) ? p[1] : p.y;
        const z = Array.isArray(p) ? p[2] : p.z;
        return `${x},${y},${z},${currentFrameData.action_label}`;
      })
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mmwave_frame_${currentFrame}_action_${currentFrameData.action_label}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Reset coordinate ranges to full data bounds
  const resetCoordinateRanges = () => {
    if (!data) return;
    
    setXRange([data.metadata.bounds.x[0], data.metadata.bounds.x[1]]);
    setYRange([data.metadata.bounds.y[0], data.metadata.bounds.y[1]]);
    setZRange([data.metadata.bounds.z[0], data.metadata.bounds.z[1]]);
  };

  // Labeling handlers
  const handleLabelAction = (action: BoxAction) => {
    dispatchLabelAction(action);
  };

  const handleLabelModeChange = (mode: Mode) => {
    setLabelingMode(mode);
  };

  const handleLabelSelectionChange = (id?: string) => {
    setSelectedBoxId(id);
  };

  const handleLabelTransformStart = () => {
    setIsDragging(true);
  };

  const handleLabelTransformEnd = () => {
    setIsDragging(false);
  };

  const handleExportLabels = () => {
    exportJSON(labelBoxes);
  };

  const handleImportLabels = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      importJSON(file)
        .then((importedBoxes) => {
          dispatchLabelAction({
            type: 'bulkSet',
            boxes: importedBoxes
          });
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        })
        .catch((error) => {
          alert(`Import failed: ${error.message}`);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        });
    }
  };

  const handleClearLabels = () => {
    if (window.confirm('Are you sure you want to clear all labels?')) {
      dispatchLabelAction({
        type: 'bulkSet',
        boxes: []
      });
      setSelectedBoxId(undefined);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          <Typography variant="h6">Error Loading Data</Typography>
          <Typography>{error}</Typography>
        </Alert>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          <Typography>No data available</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: '#1a1a1a', minHeight: '100vh' }}>
      <Typography variant="h4" gutterBottom sx={{ color: '#ffffff', mb: 3 }}>
        mmWave Radar Point Cloud Visualization
      </Typography>
      
      {/* Data Source Toggle */}
      <Box sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={useDemoData}
              onChange={(e) => setUseDemoData(e.target.checked)}
              disabled={loading}
            />
          }
          label={`Using ${useDemoData ? 'Demo' : 'Full'} Data`}
          sx={{ color: '#ffffff' }}
        />
        {useDemoData && (
          <Typography variant="body2" sx={{ color: '#cccccc', mt: 1 }}>
            Demo data contains 10 frames with 3 different actions. Switch to full data for your complete mmWave dataset.
          </Typography>
        )}
      </Box>
      
      <Grid container spacing={3}>
        {/* Main Visualization */}
        <Grid item xs={12} lg={9}>
          <Paper sx={{ p: 2, backgroundColor: '#2a2a2a' }}>
            <Box sx={{ position: 'relative', height: '70vh' }}>
              <Plot
                data={plotData}
                layout={plotLayout}
                style={{ width: '100%', height: '100%' }}
                config={{
                  displayModeBar: true,
                  displaylogo: false,
                  modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d']
                }}
              />
              
              {/* Overlay Controls */}
              <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
                <Tooltip title="Toggle Playback">
                  <IconButton
                    onClick={() => setIsPlaying(!isPlaying)}
                    sx={{ color: '#ffffff', backgroundColor: 'rgba(0,0,0,0.5)' }}
                  >
                    {isPlaying ? <Pause /> : <PlayArrow />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reset View">
                  <IconButton
                    onClick={() => setCurrentFrame(0)}
                    sx={{ color: '#ffffff', backgroundColor: 'rgba(0,0,0,0.5)' }}
                  >
                    <Refresh />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reset Coordinate Ranges">
                  <IconButton
                    onClick={resetCoordinateRanges}
                    sx={{ color: '#ffffff', backgroundColor: 'rgba(0,0,0,0.5)' }}
                  >
                    <GridOn />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download Frame">
                  <IconButton
                    onClick={handleDownload}
                    sx={{ color: '#ffffff', backgroundColor: 'rgba(0,0,0,0.5)' }}
                  >
                    <Download />
                  </IconButton>
                </Tooltip>
              </Box>
              
              {/* 3D Labeling Overlay */}
              {showLabeling && (
                <Box sx={{ 
                  position: 'absolute', 
                  top: 0, 
                  left: 0, 
                  width: '100%', 
                  height: '100%',
                  pointerEvents: 'none',
                  zIndex: 10
                }}>
                  <Canvas camera={{ position: [5, 5, 5] }}>
                    <ambientLight intensity={0.5} />
                    <pointLight position={[10, 10, 10]} />
                    
                    {/* Grid for reference */}
                    <gridHelper args={[10, 10]} />
                    
                    {/* Axes for reference */}
                    <axesHelper args={[5]} />
                    
                    {/* LabelLayer */}
                    <LabelLayer 
                      currentFrameId={currentFrame}
                      boxes={labelBoxes}
                      onTransformStart={handleLabelTransformStart}
                      onTransformEnd={handleLabelTransformEnd}
                      onBoxAction={handleLabelAction}
                      mode={labelingMode}
                      onModeChange={handleLabelModeChange}
                      selectedId={selectedBoxId}
                      onSelectionChange={handleLabelSelectionChange}
                    />
                  </Canvas>
                  
                  {/* LabelHUD */}
                  <Box sx={{ pointerEvents: 'auto' }}>
                    <LabelHUD
                      selectedId={selectedBoxId}
                      boxes={labelBoxes}
                      onBoxAction={handleLabelAction}
                      onSelectionChange={handleLabelSelectionChange}
                    />
                  </Box>
                </Box>
              )}
              
              {/* Debug Info */}
              {filteredPoints.length === 0 && (
                <Box sx={{ 
                  position: 'absolute', 
                  top: '50%', 
                  left: '50%', 
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: 'rgba(0,0,0,0.8)',
                  padding: 2,
                  borderRadius: 1
                }}>
                  <Typography sx={{ color: '#ffffff', textAlign: 'center' }}>
                    No points visible in current frame
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#cccccc', textAlign: 'center' }}>
                    Try adjusting point size or camera view
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Control Panel */}
        <Grid item xs={12} lg={3}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            
            {/* 3D Labeling Controls */}
            <Card sx={{ backgroundColor: '#2a2a2a' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Label sx={{ color: '#ffffff', mr: 1 }} />
                  <Typography variant="h6" sx={{ color: '#ffffff' }}>
                    3D Labeling
                  </Typography>
                  <Switch
                    checked={showLabeling}
                    onChange={(e) => setShowLabeling(e.target.checked)}
                    sx={{ ml: 'auto' }}
                  />
                </Box>
                
                {showLabeling && (
                  <>
                    <Button
                      variant="contained"
                      onClick={() => setLabelingMode(labelingMode === 'create' ? 'idle' : 'create')}
                      sx={{
                        backgroundColor: labelingMode === 'create' ? '#ff6b35' : '#4a4a4a',
                        color: 'white',
                        mb: 2,
                        width: '100%'
                      }}
                    >
                      {labelingMode === 'create' ? 'Exit Create Mode' : 'Create Labels'}
                    </Button>
                    
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setLabelingMode('translate')}
                        sx={{ 
                          color: labelingMode === 'translate' ? '#ff6b35' : '#ffffff',
                          borderColor: labelingMode === 'translate' ? '#ff6b35' : '#666666'
                        }}
                      >
                        Move
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setLabelingMode('rotate')}
                        sx={{ 
                          color: labelingMode === 'rotate' ? '#ff6b35' : '#ffffff',
                          borderColor: labelingMode === 'rotate' ? '#ff6b35' : '#666666'
                        }}
                      >
                        Rotate
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setLabelingMode('scale')}
                        sx={{ 
                          color: labelingMode === 'scale' ? '#ff6b35' : '#ffffff',
                          borderColor: labelingMode === 'scale' ? '#ff6b35' : '#666666'
                        }}
                      >
                        Scale
                      </Button>
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleExportLabels}
                        sx={{ color: '#4CAF50', borderColor: '#4CAF50' }}
                      >
                        Export
                      </Button>
                      
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".json"
                        onChange={handleImportLabels}
                        style={{ display: 'none' }}
                      />
                      
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => fileInputRef.current?.click()}
                        sx={{ color: '#2196F3', borderColor: '#2196F3' }}
                      >
                        Import
                      </Button>
                      
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleClearLabels}
                        sx={{ color: '#f44336', borderColor: '#f44336' }}
                      >
                        Clear
                      </Button>
                    </Box>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Chip 
                        label={`Labels: ${labelBoxes.length}`}
                        sx={{ backgroundColor: '#4a4a4a', color: '#ffffff' }}
                      />
                      <Chip 
                        label={`Mode: ${labelingMode}`}
                        sx={{ backgroundColor: '#4a4a4a', color: '#ffffff' }}
                      />
                      <Chip 
                        label={`Selected: ${selectedBoxId ? 'Yes' : 'No'}`}
                        sx={{ backgroundColor: '#4a4a4a', color: '#ffffff' }}
                      />
                    </Box>
                    
                    <Typography variant="body2" sx={{ color: '#cccccc', mt: 2, fontSize: '11px' }}>
                      <strong>Keyboard Shortcuts:</strong><br/>
                      B - Toggle create mode<br/>
                      W/E/R - Move/Rotate/Scale<br/>
                      Delete - Delete selected<br/>
                      L - Lock/Unlock selected
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Frame Navigation */}
            <Card sx={{ backgroundColor: '#2a2a2a' }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
                  Frame Navigation
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <IconButton
                    onClick={() => setCurrentFrame(0)}
                    disabled={currentFrame === 0}
                    sx={{ color: '#ffffff' }}
                  >
                    <FirstPage />
                  </IconButton>
                  <IconButton
                    onClick={() => setCurrentFrame(prev => Math.max(0, prev - 1))}
                    disabled={currentFrame === 0}
                    sx={{ color: '#ffffff' }}
                  >
                    <SkipPrevious />
                  </IconButton>
                  <Typography sx={{ color: '#ffffff', minWidth: '80px', textAlign: 'center' }}>
                    {currentFrame + 1} / {data.metadata.total_frames}
                  </Typography>
                  <IconButton
                    onClick={() => setCurrentFrame(prev => Math.min(data.metadata.total_frames - 1, prev + 1))}
                    disabled={currentFrame === data.metadata.total_frames - 1}
                    sx={{ color: '#ffffff' }}
                  >
                    <SkipNext />
                  </IconButton>
                  <IconButton
                    onClick={() => setCurrentFrame(data.metadata.total_frames - 1)}
                    disabled={currentFrame === data.metadata.total_frames - 1}
                    sx={{ color: '#ffffff' }}
                  >
                    <LastPage />
                  </IconButton>
                </Box>
                
                <Typography sx={{ color: '#ffffff', mb: 1 }}>Frame</Typography>
                <Slider
                  value={currentFrame}
                  onChange={(_, value) => setCurrentFrame(value as number)}
                  min={0}
                  max={data.metadata.total_frames - 1}
                  sx={{ mb: 2 }}
                />
                
                <Typography sx={{ color: '#ffffff', mb: 1 }}>Playback Speed (ms)</Typography>
                <Slider
                  value={playbackSpeed}
                  onChange={(_, value) => setPlaybackSpeed(value as number)}
                  min={100}
                  max={3000}
                  step={100}
                  sx={{ mb: 2 }}
                />
                
                <Chip 
                  label={`Action: ${currentFrameData?.action_label || 'N/A'}`}
                  sx={{ backgroundColor: '#4a4a4a', color: '#ffffff' }}
                />
              </CardContent>
            </Card>

            {/* Point Settings */}
            <Card sx={{ backgroundColor: '#2a2a2a' }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
                  Point Settings
                </Typography>
                
                <Typography sx={{ color: '#ffffff', mb: 1 }}>Point Size</Typography>
                <Slider
                  value={pointSize}
                  onChange={(_, value) => setPointSize(value as number)}
                  min={1}
                  max={10}
                  sx={{ mb: 2 }}
                />
                
                <Typography sx={{ color: '#ffffff', mb: 1 }}>Opacity</Typography>
                <Slider
                  value={pointOpacity}
                  onChange={(_, value) => setPointOpacity(value as number)}
                  min={0.1}
                  max={1}
                  step={0.1}
                  sx={{ mb: 2 }}
                />
                
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel sx={{ color: '#ffffff' }}>Color By</InputLabel>
                  <Select
                    value={colorBy}
                    onChange={(e) => setColorBy(e.target.value as any)}
                    sx={{ color: '#ffffff' }}
                  >
                    <MenuItem value="action">Action Label</MenuItem>
                    <MenuItem value="z_coordinate">Z Coordinate</MenuItem>
                    <MenuItem value="distance">Distance from Origin</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel sx={{ color: '#ffffff' }}>Color Scheme</InputLabel>
                  <Select
                    value={colorScheme}
                    onChange={(e) => setColorScheme(e.target.value)}
                    sx={{ color: '#ffffff' }}
                  >
                    <MenuItem value="viridis">Viridis</MenuItem>
                    <MenuItem value="plasma">Plasma</MenuItem>
                    <MenuItem value="inferno">Inferno</MenuItem>
                    <MenuItem value="magma">Magma</MenuItem>
                    <MenuItem value="turbo">Turbo</MenuItem>
                  </Select>
                </FormControl>
              </CardContent>
            </Card>

            {/* Action Statistics */}
            <Card sx={{ backgroundColor: '#2a2a2a' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: '#ffffff' }}>
                    Action Statistics
                  </Typography>
                  <IconButton
                    onClick={() => setShowStatistics(!showStatistics)}
                    sx={{ color: '#ffffff', ml: 'auto' }}
                  >
                    {showStatistics ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </Box>
                
                {showStatistics && (
                  <List dense>
                    {actionStats.map((action) => (
                      <ListItem key={action.label}>
                        <ListItemText
                          primary={`Action ${action.label}`}
                          secondary={`${action.count} frames`}
                          sx={{ color: '#ffffff' }}
                        />
                        <ListItemSecondaryAction>
                          <Chip 
                            label={action.count}
                            size="small"
                            sx={{ backgroundColor: '#4a4a4a', color: '#ffffff' }}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>

            {/* View Settings */}
            <Card sx={{ backgroundColor: '#2a2a2a' }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
                  View Settings
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={showGrid}
                      onChange={(e) => setShowGrid(e.target.checked)}
                    />
                  }
                  label="Show Grid"
                  sx={{ color: '#ffffff', mb: 1 }}
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={showAxes}
                      onChange={(e) => setShowAxes(e.target.checked)}
                    />
                  }
                  label="Show Axes"
                  sx={{ color: '#ffffff', mb: 1 }}
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={bypassFiltering}
                      onChange={(e) => setBypassFiltering(e.target.checked)}
                    />
                  }
                  label="Bypass Coordinate Filtering"
                  sx={{ color: '#ffffff', mb: 2 }}
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoRotate}
                      onChange={(e) => setAutoRotate(e.target.checked)}
                    />
                  }
                  label="Auto Rotate"
                  sx={{ color: '#ffffff', mb: 2 }}
                />
                
                {autoRotate && (
                  <>
                    <Typography sx={{ color: '#ffffff', mb: 1 }}>Rotation Speed</Typography>
                    <Slider
                      value={rotationSpeed}
                      onChange={(_, value) => setRotationSpeed(value as number)}
                      min={0.1}
                      max={2}
                      step={0.1}
                      sx={{ mb: 2 }}
                    />
                  </>
                )}
                
                <Typography sx={{ color: '#ffffff', mb: 1 }}>Camera Distance</Typography>
                <Slider
                  value={cameraDistance}
                  onChange={(_, value) => setCameraDistance(value as number)}
                  min={1}
                  max={10}
                  sx={{ mb: 2 }}
                />
              </CardContent>
            </Card>

            {/* Data Info */}
            <Card sx={{ backgroundColor: '#2a2a2a' }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
                  Data Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Chip 
                    label={`Total Frames: ${data.metadata.total_frames}`}
                    sx={{ backgroundColor: '#4a4a4a', color: '#ffffff' }}
                  />
                  <Chip 
                    label={`Current Points: ${filteredPoints.length}`}
                    sx={{ backgroundColor: '#4a4a4a', color: '#ffffff' }}
                  />
                  <Chip 
                    label={`Unique Actions: ${actionStats.length}`}
                    sx={{ backgroundColor: '#4a4a4a', color: '#ffffff' }}
                  />
                  <Chip 
                    label={`Point Size: ${pointSize}`}
                    sx={{ backgroundColor: '#4a4a4a', color: '#ffffff' }}
                  />
                  <Chip 
                    label={`Opacity: ${pointOpacity}`}
                    sx={{ backgroundColor: '#4a4a4a', color: '#ffffff' }}
                  />
                  {currentFrameData && (
                    <Chip 
                      label={`Frame ${currentFrame + 1} Points: ${currentFrameData.point_count}`}
                      sx={{ backgroundColor: '#2e7d32', color: '#ffffff' }}
                    />
                  )}
                  <Chip 
                    label={`X Range: [${xRange[0]}, ${xRange[1]}]`}
                    sx={{ backgroundColor: '#1976d2', color: '#ffffff' }}
                  />
                  <Chip 
                    label={`Y Range: [${yRange[0]}, ${yRange[1]}]`}
                    sx={{ backgroundColor: '#1976d2', color: '#ffffff' }}
                  />
                  <Chip 
                    label={`Z Range: [${zRange[0]}, ${zRange[1]}]`}
                    sx={{ backgroundColor: '#1976d2', color: '#ffffff' }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MmWaveVisualization; 